import type { SystemHealth } from '@/api/ai-chat/types'

export const OUTCOME_COLORS: Record<string, string> = {
  appointment: '#2d9e5f',
  transfer: '#2d5be3',
  dnq: '#f59e0b',
  'not-interested': '#ef4444',
  left: '#9ca3af',
  dnc: '#ec4899',
  'in-progress': '#8b5cf6',
}

export const OUTCOME_TAG_COLORS: Record<string, string> = {
  appointment: 'success',
  transfer: 'blue',
  dnq: 'gold',
  'not-interested': 'error',
  left: 'default',
  dnc: 'magenta',
  'in-progress': 'purple',
}

export const STAGE_NAMES = [
  '',
  'Opening',
  'Debt Amt',
  'Payments',
  'Hardship',
  'Scheduling',
  'Confirmed',
]

export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export const SYS_LABELS: Record<string, string> = {
  postgres: 'PostgreSQL',
  embedder: 'Embedder',
  salesforce: 'Salesforce',
  load: 'CPU Load',
  ram: 'RAM',
  disk: 'Disk',
}

export const SYS_STATUS_MAP: Record<
  string,
  'success' | 'warning' | 'error' | 'default'
> = {
  healthy: 'success',
  degraded: 'warning',
  down: 'error',
  unknown: 'default',
}

export function fmtMsgTime(ts: string): string {
  if (!ts) return ''
  const d = new Date(ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z')
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  return sameDay
    ? d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      })
    : d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      })
}

export function fmtTime(ts: string | null | undefined): string {
  if (!ts) return '—'
  return new Date(
    ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z',
  ).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function fmtDuration(secs: number | null | undefined): string {
  if (!secs) return '—'
  if (secs < 60) return secs + 's'
  if (secs < 3600) return Math.round(secs / 60) + 'm'
  return Math.round(secs / 3600) + 'h'
}

export function fmtModel(model: string | null | undefined): string {
  if (!model) return '—'
  if (model.includes('haiku')) return 'Haiku'
  if (model.includes('sonnet')) return 'Sonnet'
  if (model.includes('gpt') || model.includes('openai')) return 'GPT-4o-mini'
  return model
}

export function fmtDevice(d: string | null | undefined): string {
  const map: Record<string, string> = {
    iPhone: '📱 iPhone',
    iPad: '📱 iPad',
    Android: '📱 Android',
    'Android Tab': '📱 Android Tab',
    WinPhone: '📱 WinPhone',
    Mac: '🖥️ Mac',
    Windows: '🖥️ Windows',
    Linux: '🖥️ Linux',
  }
  return d ? (map[d] ?? d) : '—'
}

export function fmtSysValue(s: SystemHealth): string {
  if (s.status !== 'healthy' && s.last_message) return s.last_message
  if (s.last_value == null) return s.status
  if (s.service === 'load') return 'load ' + s.last_value
  if (s.service === 'ram') return s.last_value + '% used'
  if (s.service === 'disk') return s.last_value + '% used'
  return s.status
}

// ── Diff algorithm ─────────────────────────────────────────────────────────

export type DiffLine = { type: 'ins' | 'del' | 'ctx'; text: string }

function lcsTable(a: string[], b: string[]): Uint32Array[] {
  const m = a.length,
    n = b.length
  const dp: Uint32Array[] = Array.from(
    { length: m + 1 },
    () => new Uint32Array(n + 1),
  )
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1])
  return dp
}

export function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split('\n'),
    b = newText.split('\n')
  const dp = lcsTable(a, b)
  const result: DiffLine[] = []
  let i = a.length,
    j = b.length
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      result.unshift({ type: 'ctx', text: a[i - 1] })
      i--
      j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: 'ins', text: b[j - 1] })
      j--
    } else {
      result.unshift({ type: 'del', text: a[i - 1] })
      i--
    }
  }
  return result
}
